import { type TFunction } from "i18next";
import _ from "lodash";

import apolloClient from "@/apollo";
import { addDangerFlash, addSuccessFlash } from "@/components/flash/flashReducer";
import { type ContactStatusFragment, EventTypesDocument, MutateEventDocument, MutateTaskDocument } from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import store from "@/store";
import { naiveDateTime } from "@/utils/dates";

import type { ValuesType } from "./formUtils";

async function createFollowUp(session: SessionInterface, values: ValuesType, t: TFunction) {
  const { data: eventTypeData } = await apolloClient.query({
    query: EventTypesDocument,
    variables: {
      customerId: session.currentCustomer.id,
      projectId: session.currentProject.id,
    },
  });

  const eventType = eventTypeData?.eventTypes.find((item) => item.type === "FOLLOWUP");

  if (!eventType) {
    store.dispatch(addDangerFlash(t("gju:tasks.follow_up_type_missing")));
    return;
  }

  const { data } = await apolloClient.mutate({
    mutation: MutateEventDocument,
    variables: {
      customerId: session.currentCustomer.id,
      projectId: session.currentProject.id,
      event: {
        subject: t("gju:tasks.follow_up_subject"),
        allDay: false,
        startTime: naiveDateTime(values.attrs.followUp),
        endTime: naiveDateTime(values.attrs.followUp),
        contactId: values.contactId,
        ownerId: session.currentUser.id,
        typeId: eventType.id,
      },
    },
  });

  if (!data?.mutateEvent) throw new MutationError();
}

export async function saveTask(
  session: SessionInterface,
  existingTask: Nilable<CCTaskInput>,
  _states: ContactStatusFragment[],
  values: ValuesType,
  t: TFunction,
) {
  try {
    if (values.attrs.followUp && !existingTask?.id) {
      await createFollowUp(session, values, t);
    }

    const { data } = await apolloClient.mutate({
      mutation: MutateTaskDocument,
      variables: {
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
        id: existingTask?.id,
        values: _.omit(values, ["contact"]),
      },
    });

    if (!data?.mutateTask) throw new MutationError();

    store.dispatch(
      addSuccessFlash(t(existingTask?.id ? "customerContacts:edit_form.updated" : "customerContacts:new_form.created")),
    );
    return data.mutateTask;
  } catch (error) {
    store.dispatch(addDangerFlash(t("translation:global.general_error")));
    handleError(error);
  }
}
